import { v4 as uuid } from 'uuid';
import TYPES from '@/types';
import Vue from 'vue';
import { clabe } from 'clabe-validator';
import { convertToLargerPossibleUnit } from '@/vue-app/utils/files';
import { clabeFormat, requiredRule } from '@/vue-app/utils/form-rules';

// Infrastructure
import Functions from '@/modules/shared/infrastructure/utils/functions';

// Application
import SearchAllianzFinancialInstitutionsQuery
  from '@/modules/my-investment/catalogs/allianz/financial-institution/application/queries/search-financial-institutions-query';
import CustomerBankAccountChooseYourDocumentsService
  from '@/modules/user/customer-bank-account/application/services/customer-bank-account-choose-your-documents-service';
import UpdateCustomerBankAccountCommand
  from '@/modules/user/customer-bank-account/application/commands/update-customer-bank-account-command';

// Domain
import { CustomerBankAccountDto }
  from '@/modules/user/customer-bank-account/domain/dtos/customer-bank-account-dto';
import {
  FinancialInstitutionEntity as AllianzFinancialInstitutionEntity,
} from '@/modules/my-investment/catalogs/allianz/financial-institution/domain/entities/financial-institution-entity';
import Inject from '@/modules/shared/domain/di/inject';
import Translator from '@/modules/shared/domain/i18n/translator';
import { Values } from '@/modules/shared/domain/i18n/types';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';

export default class UpdateBankAccountDataViewModel {
  @Inject(TYPES.SEARCH_CATALOG_ALLIANZ_FINANCIAL_INSTITUTION_QUERY)
  private readonly search_allianz_financial_institutions_query!:
    SearchAllianzFinancialInstitutionsQuery;

  @Inject(TYPES.UPDATE_CUSTOMER_BANK_ACCOUNT_COMMAND)
  private readonly update_customer_bank_account_command!: UpdateCustomerBankAccountCommand;

  @Inject(TYPES.CUSTOMER_BANK_ACCOUNT_CHOOSE_YOUR_DOCUMENTS_SERVICE)
  private readonly choose_your_documents_service!: CustomerBankAccountChooseYourDocumentsService;

  @Inject(TYPES.I18N)
  readonly translator!: Translator;

  @Inject(TYPES.NOTIFIER)
  private readonly message_notifier!: MessageNotifier;

  @Inject(TYPES.UTIL_FUNCTIONS)
  readonly functions!: Functions;

  readonly view!: Vue;

  readonly i18n_namespace = 'components.user-profile.user-profile-tabs.user-profile-my-documents-tab.update-bank-account.data';

  public constructor(view: Vue) {
    this.view = view;
  }

  translate = (message: string, values?: Values) => this.translator.translate(`${this.i18n_namespace}.${message}`, values);

  readonly limit_file_size = 4 * 1024 * 1024;

  small_description = this.translate('description_3');

  bank_account_proof: File | null = null;

  invalid_file = true;

  is_loading = false;

  is_valid_form = false;

  minimum_file_size = 0.1;

  allowed_extensions = ['image/jpeg', 'image/jpg', 'image/png'];

  bank_account_proof_accept = 'image/png, image/jpeg, image/jpg';

  bank_institution = '';

  bank = {
    clabe: '',
    code: '',
  };

  rules = {
    clabe: [requiredRule, clabeFormat],
    bank_institution: [requiredRule],
  };

  institution_payload: AllianzFinancialInstitutionEntity = {
    codEntFinan: '',
    cveBancaria: '',
    mtoMaxEnvio: '',
    mtoMinEnvio: '',
    descEntFinan: '',
  }

  allianz_financial_institutions: Array<AllianzFinancialInstitutionEntity> = [];

  initialize = async () => {
    const { pocket_option_is_checked, wealth_option_is_checked } = this
      .choose_your_documents_service.getChooseYourDocumentsData();
    if (pocket_option_is_checked && wealth_option_is_checked) {
      this.small_description += `${this.translate('allianz')} y ${this.translate('kuspit')}.`;
    } else {
      this.small_description += (wealth_option_is_checked) ? `${this.translate('allianz')}.` : `${this.translate('kuspit')}.`;
    }
    await this.loadFinancialInstitutions();
  }

  get styled_file_name(): string {
    if (this.bank_account_proof) {
      return `${this.bank_account_proof.name.slice(0, 30)}${this.bank_account_proof.name.length > 35 ? '...' : ''}`;
    }
    return this.translate('bank_account.placeholder');
  }

  get styled_file_size(): string {
    if (this.bank_account_proof) {
      return convertToLargerPossibleUnit(this.bank_account_proof.size);
    }
    return '0 kb';
  }

  get is_the_information_valid() {
    return this.is_valid_form && !this.invalid_file;
  }

  changeClabe = () => {
    if (this.bank.clabe.length >= 3) {
      this.bank_institution = '';
      this.setFinancialInstitutions();
    }
  }

  setFinancialInstitutions = () => {
    if (this.bank.clabe) {
      const clabe_without_space = this.bank.clabe.replace(/\s/g, '');
      const validation = clabe.validate(clabe_without_space);
      const bank_code = validation.code.bank;
      const is_valid = validation.formatOk;
      if (is_valid) {
        const search_bank = this.allianz_financial_institutions.find(
          (bank) => bank.cveBancaria === bank_code,
        );
        if (search_bank) {
          this.institution_payload = { ...search_bank };
          this.bank_institution = this.format_bank_institution(search_bank.descEntFinan);
        } else {
          this.message_notifier.showErrorNotification(this.translate('errors.financial_institution_not_found'));
        }
      }
    }
  }

  format_bank_institution = (bank_institution: string) => {
    let formatted_bank_institution = bank_institution;
    const internal_key_position = bank_institution.indexOf('SW');
    const dom_position = bank_institution.indexOf('(DOM');
    if (internal_key_position >= 0) {
      formatted_bank_institution = bank_institution.substring(0, internal_key_position);
    }
    if (dom_position >= 0) {
      formatted_bank_institution = bank_institution.substring(0, dom_position);
    }
    return formatted_bank_institution;
  }

  loadFinancialInstitutions = async () => {
    try {
      this.allianz_financial_institutions = await this
        .search_allianz_financial_institutions_query.execute();
    } catch {
      this.message_notifier.showErrorNotification('errors.load_financial_institution');
    }
  }

  handleBankAccountProof = (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target.files) {
      const [file] = target.files;
      if (file.size <= this.limit_file_size && file.size >= this.minimum_file_size
        && this.allowed_extensions.indexOf(file.type) > -1) {
        this.bank_account_proof = file;
        this.invalid_file = false;
      } else {
        this.bank_account_proof = null;
        this.invalid_file = true;
        this.message_notifier.showErrorNotification(this.translate('errors.invalid_file'));
      }
    }
  }

  convertFileToBase64 = async (selected_file: File) => (
    this.functions.convert_file_to_base_64(selected_file)
  );

  updateBankAccountInformation = async () => {
    try {
      if (this.bank_account_proof) {
        const base_64_file = await this.convertFileToBase64(this.bank_account_proof);
        if (base_64_file) {
          this.is_loading = true;
          const { selected_investment_product_ids, wealth_option_is_checked } = this
            .choose_your_documents_service.getChooseYourDocumentsData();
          const payload: CustomerBankAccountDto = {
            request_id: uuid(),
            investment_product_ids: selected_investment_product_ids,
            bank_account: {
              account_number: this.bank.clabe.replace(/\s/g, ''),
              bank_name: this.bank_institution,
            },
            account_statement: {
              name: this.bank_account_proof.name,
              mime_type: this.bank_account_proof.type,
              file_data: base_64_file.toString(),
            },
          };
          await this.update_customer_bank_account_command.execute(payload);
          this.view.$emit('endProcess', wealth_option_is_checked);
        }
      }
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.update_customer_bank_account_command'));
    } finally {
      this.is_loading = false;
    }
  }

  prevStep = () => {
    this.view.$emit('prevStep');
  }
}
